<script setup lang="ts">
import { convertToLocalTime, convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import { useLineChart } from '@/composables/useLineChart'
import ChartMarker from '@/components/Globals/Charts/ChartMarker.vue'
import type { ILineChartMarker } from '@/interfaces/components/chats/chart-marker'
import type { ISensorTotalizer } from '@/interfaces/stores/dashboard'
import { useDashboardStore } from '@/store/dashboard'
import {
  formatDashboardPeriodRangeLabel,
  getDashboardPeriodKey,
  normalizeDashboardPeriod,
} from '@/utils/dashboard-workspace'
import { isMobileWindow } from '@/utils/screen/window'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import VueApexCharts from 'vue3-apexcharts'

const props = defineProps<{
  periodKey: string
  compactLayout: boolean
}>()

const { t } = useI18n()
const dashboardStore = useDashboardStore()
const { searches } = storeToRefs(dashboardStore)

const searchesInGroup = computed(() =>
  searches.value.filter((s) => s.visible && s.type === 'sensor' && getDashboardPeriodKey(s) === props.periodKey),
)

const series = ref<Array<{ name: string; data: { x: Date; y: number }[] }>>([])
const chartOptions = ref<Record<string, unknown>>({})
const chartLoaded = ref(false)
const fixedMarkers = ref<ILineChartMarker[]>([])

const chartIdSafe = computed(() => `u-${props.periodKey.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 80)}`)

const sectionDomId = computed(() => `dashboard-unified-${chartIdSafe.value}`)

/** Modo lado a lado: gráfico unificado mais baixo para alinhar ao bloco compacto. */
const unifiedChartHeightPx = computed(() => (props.compactLayout ? 280 : 360))

const periodRangeLabel = computed(() => {
  const first = searchesInGroup.value[0]
  return first ? formatDashboardPeriodRangeLabel(first.period) : ''
})


const seriesLegendCards = computed(() => {
  if (!chartLoaded.value || !series.value.length) return []
  const cols = (chartOptions.value.colors as string[] | undefined) ?? []
  return series.value.map((s, i) => ({
    name: s.name,
    color: cols[i] ?? '#75787B',
  }))
})

function rebuildChart() {
  fixedMarkers.value = []
  const list = searchesInGroup.value
  if (list.length < 2) {
    chartLoaded.value = false
    series.value = []
    return
  }

  const seriesOut: Array<{ name: string; data: { x: Date; y: number }[] }> = []
  const colorsOut: string[] = []

  for (const s of list) {
    const t = s.totalizer as ISensorTotalizer
    const data =
      t.readings?.map((r) => {
        const rawTime = (r as { time: string | number | Date }).time
        const timeStr = rawTime instanceof Date ? rawTime.toISOString() : String(rawTime)
        return {
          x: convertToLocalTime(timeStr),
          y: Number(r.total_power),
        }
      }) ?? []
    if (data.length) {
      seriesOut.push({ name: s.label, data })
      colorsOut.push(s.color)
    }
  }

  if (!seriesOut.length) {
    chartLoaded.value = false
    series.value = []
    return
  }

  const period = normalizeDashboardPeriod(list[0].period)
  const [startDate, endDate] = period
  const min = startDate ? convertToLocalTimeUnix(startDate) : undefined
  const max = endDate ? convertToLocalTimeUnix(endDate) : undefined

  const { chartOptions: baseOptions } = useLineChart({
    chartId: chartIdSafe.value,
    exportConfig: { filename: 'dashboard-demanda-unificada', unit: 'kW' },
    yAxisFormatter: (value: number) => `${value.toFixed(2)} kW`,
    tooltipYFormatter: (value: number) => `${value?.toFixed(2)} kW`,
    unit: 'kW',
  })

  const baseChart = (baseOptions.value as { chart?: Record<string, unknown> }).chart ?? {}
  chartOptions.value = {
    ...baseOptions.value,
    chart: {
      ...baseChart,
      redrawOnParentResize: true,
    },
    colors: colorsOut,
    legend: {
      show: true,
      fontSize: props.compactLayout ? '11px' : '14px',
      fontWeight: 'bold',
      labels: { useSeriesColors: true },
      position: 'bottom',
      offsetY: props.compactLayout ? 4 : 10,
    },
    stroke: { width: props.compactLayout ? 2 : 3, curve: 'straight' },
    xaxis: {
      ...baseOptions.value.xaxis,
      min,
      max,
      labels: {
        ...(baseOptions.value.xaxis as { labels?: Record<string, unknown> }).labels,
        rotate: props.compactLayout ? -45 : 0,
        format: 'dd/MM HH:mm',
        ...(props.compactLayout ? { style: { fontSize: '11px', fontWeight: 500 } } : {}),
      },
      tooltip: {
        ...(baseOptions.value.xaxis as { tooltip?: Record<string, unknown> }).tooltip,
      },
    },
    tooltip: {
      ...(baseOptions.value.tooltip as object),
      x: { format: 'dd/MM HH:mm' },
    },
  }

  series.value = seriesOut
  chartLoaded.value = true
}

watch(
  () => [props.periodKey, props.compactLayout, searches.value] as const,
  () => {
    rebuildChart()
  },
  { deep: true, immediate: true },
)

function handleChartClick(
  event: { target: { getBoundingClientRect: () => DOMRect }; clientX: number; clientY: number },
  _chartContext: unknown,
  config: { dataPointIndex: number; seriesIndex: number },
) {
  if (isMobileWindow()) return
  if (config.dataPointIndex < 0 || config.seriesIndex < 0) return
  const ser = series.value[config.seriesIndex]
  if (!ser?.data?.[config.dataPointIndex]) return
  const dataPoint = ser.data[config.dataPointIndex]
  const chartRect = event.target.getBoundingClientRect()
  fixedMarkers.value.push({
    seriesName: String(ser.name ?? ''),
    x: dataPoint.x,
    y: `${dataPoint.y} kW`,
    positionX: event.clientX - chartRect.left,
    positionY: event.clientY - chartRect.top + 50,
  })
}

function onChartClicked(event: unknown, chartContext: unknown, config: { dataPointIndex: number; seriesIndex: number }) {
  handleChartClick(
    event as { target: { getBoundingClientRect: () => DOMRect }; clientX: number; clientY: number },
    chartContext,
    config,
  )
}

function clearMarkers() {
  fixedMarkers.value = []
}
</script>

<template>
  <section
    v-if="chartLoaded && series.length"
    :id="sectionDomId"
    class="min-w-0 max-w-full border border-stroke bg-white shadow-md"
    :class="compactLayout ? 'p-3' : 'p-4'">
    <header
      class="flex min-w-0 flex-col gap-2 border-b border-stroke"
      :class="compactLayout ? 'mb-2 pb-2' : 'mb-4 pb-4'">
      <div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
        <h3 class="shrink-0 font-satoshi text-base font-bold text-black">{{ t('dashboard.chart.unified_title') }}</h3>
        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <div
            v-for="(card, idx) in seriesLegendCards"
            :key="`${card.name}-${idx}`"
            class="flex min-w-0 max-w-[14rem] items-center gap-2 border border-stroke bg-gray-2 px-2 py-1">
            <span class="h-3.5 w-3.5 shrink-0 border border-stroke" :style="{ backgroundColor: card.color }" aria-hidden="true" />
            <span class="truncate text-xs font-semibold text-black">{{ card.name }}</span>
          </div>
        </div>
      </div>
      <p v-if="periodRangeLabel" class="text-sm font-medium text-gray-600">
        {{ t('dashboard.chart.unified_period', { range: periodRangeLabel }) }}
      </p>
    </header>
    <div
      class="relative min-w-0 max-w-full overflow-hidden bg-white"
      :style="{ minHeight: `${unifiedChartHeightPx}px` }">
      <VueApexCharts
        :key="`unified-${periodKey}-${compactLayout}`"
        :height="unifiedChartHeightPx"
        width="100%"
        class="min-w-0 max-w-full"
        :options="chartOptions"
        :series="series"
        @click="onChartClicked"
        @updated="clearMarkers"
        @zoomed="clearMarkers"
        @panned="clearMarkers" />
      <ChartMarker
        v-for="(marker, index) in fixedMarkers"
        :key="index"
        :marker="marker"
        @remove="fixedMarkers.splice(index, 1)" />
    </div>
  </section>
</template>
